import { useEffect, useRef, useState } from "preact/hooks";
import { mediaErrorReason } from "./media/failureReason.js";
import { latencyBucket, mediaErrorClass } from "./telemetry/env.js";

// Like mobile's playback waveform, these bars illustrate progress rather than
// claiming to be a decoded sample of the recording's amplitude.
const BAR_HEIGHTS = Array.from({ length: 46 }, (_, index) =>
  18 + ((index * 37 + index * index * 13) % 73));

function clockTime(seconds) {
  const safe = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, "0")}`;
}

export function AudioPlayer({ src, label, surface, telemetry, failureReason }) {
  const ref = useRef(null);
  const started = useRef(false);
  const reported = useRef(false);
  const failureReported = useRef(false);
  const playRequestedAt = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const player = ref.current;
    return () => player?.pause();
  }, [attempt]);

  const syncTime = () => {
    const player = ref.current;
    if (!player) return;
    setPosition(player.currentTime || 0);
    setDuration(Number.isFinite(player.duration) ? player.duration : 0);
  };
  const stopAnimation = () => { setPlaying(false); setLoading(false); };
  // Each player reports its outcome once. `extra` may be a promise of extra properties.
  // Each playback start, failure and stall is counted.
  const reportOutcome = (outcome, extra) => {
    if (reported.current) return;
    reported.current = true;
    Promise.resolve(extra)
      .catch(() => undefined)
      .then((props) => telemetry.count("media_viewed", { kind: "audio", outcome, media_surface: surface, ...props }));
  };
  const reportStall = () => {
    if (started.current && telemetry.allowStall(ref.current)) {
      telemetry.count("video_playback", { kind: "audio", phase: "stall", error_class: "none" });
    }
  };
  // A failed source can fire an error event and reject play(). Only the first one counts.
  const fail = (code) => {
    stopAnimation();
    setFailed(true);
    if (failureReported.current) return;
    failureReported.current = true;
    reportOutcome("error", Promise.resolve(failureReason ? failureReason(src) : null)
      .catch(() => null)
      .then((reason) => ({ reason: reason ?? mediaErrorReason(code) })));
    telemetry.count("video_playback", { kind: "audio", phase: "failure", error_class: mediaErrorClass(code) });
  };
  const toggle = async () => {
    const player = ref.current;
    if (!player) return;
    if (!player.paused) { player.pause(); return; }
    setLoading(true);
    try { await player.play(); } catch (error) {
      if (error.name !== "AbortError") fail();
    }
  };
  const progress = duration > 0 ? Math.min(1, position / duration) : 0;

  return (
    <div className="inline-attachment audio audio-player" onClick={(event) => event.stopPropagation()}>
      <audio
        key={attempt}
        ref={ref}
        src={src}
        preload="none"
        disableRemotePlayback
        x-webkit-airplay="deny"
        aria-label={label}
        onLoadedMetadata={syncTime}
        onDurationChange={syncTime}
        onTimeUpdate={syncTime}
        onSeeking={() => { setPlaying(false); setLoading(true); }}
        onSeeked={() => {
          syncTime();
          setLoading(false);
          setPlaying(!ref.current?.paused);
        }}
        onPlay={(event) => {
          if (playRequestedAt.current === null) playRequestedAt.current = performance.now();
          document.querySelectorAll("audio, video").forEach((player) => {
            if (player !== event.currentTarget) player.pause();
          });
        }}
        onPlaying={() => {
          setFailed(false);
          setLoading(false);
          setPlaying(true);
          if (started.current) return;
          started.current = true;
          const t0 = playRequestedAt.current;
          reportOutcome("ok", t0 === null ? undefined : { latency_bucket: latencyBucket(performance.now() - t0) });
          telemetry.count("video_playback", { kind: "audio", phase: "start", error_class: "none" });
        }}
        onWaiting={() => { setPlaying(false); setLoading(true); reportStall(); }}
        onStalled={reportStall}
        onPause={stopAnimation}
        onEnded={() => { stopAnimation(); syncTime(); }}
        onError={(event) => fail(event.currentTarget.error?.code)}
      />
      <button className="audio-toggle" type="button"
        aria-label={`${playing || loading ? "Pause" : "Play"} ${label}`}
        disabled={failed} onClick={toggle}>
        <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
          {playing || loading
            ? <path d="M6 4h4v16H6zm8 0h4v16h-4z" />
            : <path d="M8 4v16l13-8z" />}
        </svg>
      </button>
      <div className="audio-track">
        <div className="audio-meta">
          <span className="audio-label">{label}</span>
          <span className="audio-status">{failed ? "Unavailable" : loading ? "Loading…" : playing ? "Playing" : duration > 0 && position >= duration ? "Finished" : position > 0 ? "Paused" : ""}</span>
        </div>
        <div className={playing ? "audio-waveform playing" : "audio-waveform"}>
          <div className="waveform-bars" aria-hidden="true">
            {BAR_HEIGHTS.map((height, index) => (
              <span key={index} className={progress > 0 && index / 45 <= progress ? "waveform-bar played" : "waveform-bar"}
                style={{ height: `${height}%`, animationDelay: `${-(index % 7) * 0.13}s` }} />
            ))}
          </div>
          <input type="range" min="0" max={duration || 1} step="0.01" value={position}
            disabled={!duration || failed}
            aria-label={`Seek ${label}`}
            aria-valuetext={`${clockTime(position)} of ${clockTime(duration)}`}
            onInput={(event) => {
              const next = Number(event.currentTarget.value);
              ref.current.currentTime = next;
              setPosition(next);
            }} />
        </div>
        {/* Times stay hidden until there is something to measure, instead of "0:00 —". */}
        {duration > 0 || position > 0 ? (
          <div className="audio-times"><span>{clockTime(position)}</span><span>{duration ? clockTime(duration) : ""}</span></div>
        ) : null}
      </div>
      {failed ? (
        <div className="media-unavailable" role="status">
          <p>This audio couldn’t play. Try again, or use a browser that supports this recording.</p>
          <button className="text-button" type="button" onClick={() => {
            started.current = false;
            failureReported.current = false;
            playRequestedAt.current = null;
            setFailed(false); setPosition(0); setDuration(0); setAttempt((value) => value + 1);
          }}>Try again</button>
        </div>
      ) : null}
    </div>
  );
}
